/**
 * Bundle 101 (URGENT) - Per-event Survey Respondents page.
 *
 * Route: /admin/events/:eventId/survey/respondents (super_admin only)
 *
 * Bundle 105 - full row-click modal wired to the new
 * `/admin/events/:eventId/survey/response/:responseId` endpoint.
 * The modal renders every answer paired with its question label,
 * masks PII by default, and offers an inline unmask that requires
 * a super_admin re-confirm before revealing values.
 *
 * Data sources:
 *   GET /api/admin/events/{event_id}/survey/respondents (Bundle 98)
 *   GET /api/admin/events/{event_id}/survey/response/{response_id}
 *       (Bundle 105)
 *
 * No em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Eye, EyeOff, Download, ChevronLeft, AlertTriangle } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const STATUS_BADGE = {
  submitted: 'bg-success-50 text-success-700 border-success-200',
  started: 'bg-blue-50 text-blue-800 border-blue-200',
  invited: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  pending: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};

const ATT_BADGE = {
  checked_in: 'bg-success-50 text-success-700 border-success-200',
  not_checked_in: 'bg-neutral-100 text-neutral-500 border-neutral-200',
  claim_pending: 'bg-yellow-50 text-yellow-800 border-yellow-200',
};

// Bundle 105 - human label for a Bundle 104 drift-reason token.
const _driftLabel = (reason) => {
  if (!reason) return null;
  if (reason.startsWith('response_discovery_key_pre_bundle_89:')) {
    const v = reason.split(':', 2)[1];
    return `Pre-Bundle-89 discovery key ("${v}") - answered on old FE.`;
  }
  if (reason.startsWith('response_future_event_key_pre_bundle_89:')) {
    const v = reason.split(':', 2)[1];
    return `Pre-Bundle-89 future-event key ("${v}") - old FE.`;
  }
  if (reason === 'response_future_event_was_numeric_rating') {
    return 'Numeric rating for future_event_likelihood - pre-Bundle-89 renderer.';
  }
  if (reason.startsWith('response_connection_intent_pre_bundle_89:')) {
    const v = reason.split(':', 2)[1];
    return `Pre-Bundle-89 connection_intent value ("${v}").`;
  }
  if (reason.startsWith('response_testimonial_permission_pre_bundle_89:')) {
    const v = reason.split(':', 2)[1];
    return `Pre-Bundle-89 testimonial_permission value ("${v}").`;
  }
  return reason;
};

const AdminSurveyRespondentsPage = () => {
  const { eventId } = useParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [unmasked, setUnmasked] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [attFilter, setAttFilter] = useState('all');
  const [testFilter, setTestFilter] = useState('exclude');
  const [detail, setDetail] = useState(null);
  const [event, setEvent] = useState(null);

  const fetchRows = async (opts = {}) => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 500 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (testFilter === 'only') params.is_test = 1;
      if (testFilter === 'exclude') params.is_test = 0;
      if (opts.unmask) params.unmask = 1;
      const { data } = await api.get(
        `/admin/events/${eventId}/survey/respondents`,
        { params },
      );
      let out = data.rows || [];
      if (attFilter !== 'all') out = out.filter((r) => r.attendance_status === attFilter);
      setRows(out);
      setTotal(data.total || 0);
      if (opts.unmask) setUnmasked(true);
    } catch (e) {
      toast.error('Could not load respondents.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    api.get(`/admin/events/${eventId}`).then(({ data }) => setEvent(data?.event || null)).catch(() => {});
  }, [eventId]);

  const summary = useMemo(() => {
    const invited = rows.length;
    const submitted = rows.filter((r) => r.status === 'submitted').length;
    const inProgress = rows.filter((r) => r.status === 'started').length;
    const rate = invited ? Math.round((submitted / invited) * 1000) / 10 : 0;
    return { invited, submitted, inProgress, rate };
  }, [rows]);

  const exportCsv = () => {
    const header = ['name', 'email', 'attendance', 'status',
                     'is_test', 'started_at', 'submitted_at'];
    const lines = [header.join(',')];
    rows.forEach((r) => {
      const cells = [r.full_name, r.email, r.attendance_status,
                       r.status, r.is_test ? '1' : '0',
                       r.opened_at || '', r.submitted_at || ''];
      lines.push(cells.map((c) => `"${(c || '').toString().replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `survey-respondents-${eventId}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openDetail = async (row) => {
    setDetail({ loading: true, row, unmasked: false });
    try {
      const rid = row.response_id;
      if (!rid) {
        setDetail({ loading: false, row, detail: null,
                       unmasked: false, error: 'No response yet.' });
        return;
      }
      const { data } = await api.get(
        `/admin/events/${eventId}/survey/response/${rid}`,
      );
      setDetail({ loading: false, row, detail: data,
                     unmasked: false });
    } catch (e) {
      setDetail({ loading: false, row, detail: null,
                     unmasked: false,
                     error: 'Could not load this response.' });
    }
  };

  const requestUnmask = async () => {
    if (!detail?.detail?.response_id) return;
    // Explicit super-admin re-confirm before revealing PII.
    const ok = window.confirm(
      'Reveal PII (email, phone, contacts) for this response? '
      + 'This action is audited.',
    );
    if (!ok) return;
    try {
      const { data } = await api.get(
        `/admin/events/${eventId}/survey/response/${detail.detail.response_id}`,
        { params: { unmask: 1 } },
      );
      setDetail({ ...detail, detail: data, unmasked: true });
    } catch (e) {
      toast.error('Could not unmask this response.');
    }
  };

  return (
    <div className="p-8 space-y-6 max-w-7xl"
          data-testid="admin-survey-respondents-page">
      <div className="flex items-center gap-2 text-xs text-neutral-500">
        <Link to="/admin/surveys" className="hover:text-primary-900">
          <ChevronLeft className="h-3 w-3 inline" /> Survey Ops
        </Link>
      </div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900">
            Respondents
          </h1>
          <p className="text-neutral-600 text-sm">
            {event?.title || 'Event'} - who was invited, who answered.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/admin/events/${eventId}/survey/campaign`}>
            <Button variant="outline"
                      data-testid="respondents-campaign-btn">
              Campaign status
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={() => (unmasked ? setUnmasked(false)
                                   : fetchRows({ unmask: true }))}
            data-testid="respondents-unmask-btn"
          >
            {unmasked ? <EyeOff className="h-4 w-4 mr-2" />
                       : <Eye className="h-4 w-4 mr-2" />}
            {unmasked ? 'Mask emails' : 'Unmask emails'}
          </Button>
          <Button variant="outline" onClick={exportCsv}
                    data-testid="respondents-csv-btn">
            <Download className="h-4 w-4 mr-2" /> CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3"
            data-testid="respondents-summary">
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Total invited</div>
          <div className="text-2xl font-display font-bold text-primary-900 mt-1"
                data-testid="summary-invited">{summary.invited}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Submitted</div>
          <div className="text-2xl font-display font-bold text-primary-900 mt-1"
                data-testid="summary-submitted">{summary.submitted}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500">In progress</div>
          <div className="text-2xl font-display font-bold text-primary-900 mt-1"
                data-testid="summary-in-progress">{summary.inProgress}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Submission rate</div>
          <div className="text-2xl font-display font-bold text-primary-900 mt-1"
                data-testid="summary-rate">{summary.rate}%</div>
        </CardContent></Card>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); }}>
          <SelectTrigger className="w-44" data-testid="respondents-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All response statuses</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="started">In progress</SelectItem>
            <SelectItem value="invited">Invited (no start)</SelectItem>
            <SelectItem value="pending">Pending send</SelectItem>
          </SelectContent>
        </Select>
        <Select value={attFilter} onValueChange={(v) => setAttFilter(v)}>
          <SelectTrigger className="w-44" data-testid="respondents-att-filter">
            <SelectValue placeholder="Attendance" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All attendance</SelectItem>
            <SelectItem value="checked_in">Checked in</SelectItem>
            <SelectItem value="not_checked_in">Not checked in</SelectItem>
            <SelectItem value="claim_pending">Claim pending</SelectItem>
          </SelectContent>
        </Select>
        <Select value={testFilter} onValueChange={(v) => { setTestFilter(v); }}>
          <SelectTrigger className="w-44" data-testid="respondents-test-filter">
            <SelectValue placeholder="Test rows" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="exclude">Exclude test rows</SelectItem>
            <SelectItem value="only">Only test rows</SelectItem>
            <SelectItem value="all">Include test rows</SelectItem>
          </SelectContent>
        </Select>
        <Button
          onClick={() => fetchRows({ unmask: unmasked })}
          className="bg-primary-900 text-white hover:bg-primary-800"
          data-testid="respondents-apply-btn"
        >Apply</Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="respondents-table">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-widest text-neutral-500">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Email</th>
                <th className="p-3">Attendance</th>
                <th className="p-3">Response</th>
                <th className="p-3">Started</th>
                <th className="p-3">Submitted</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-6 text-center text-neutral-500">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-neutral-500"
                        data-testid="respondents-empty">
                    No respondents match these filters.
                    {total > 0 ? (
                      <div className="text-xs mt-1">
                        (Total invitations for this event: {total}. Try relaxing filters.)
                      </div>
                    ) : null}
                  </td>
                </tr>
              ) : rows.map((r) => (
                <tr key={r.invitation_id}
                      className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                      onClick={() => openDetail(r)}
                      data-testid={`respondent-row-${r.invitation_id}`}>
                  <td className="p-3">
                    <div className="text-primary-900 font-medium">{r.full_name}</div>
                    {r.is_test ? (
                      <Badge className="bg-yellow-50 text-yellow-800 border-yellow-200 border mt-1"
                              data-testid="respondent-is-test-badge">test</Badge>
                    ) : null}
                  </td>
                  <td className="p-3 font-mono text-xs text-neutral-700">{r.email}</td>
                  <td className="p-3">
                    <Badge className={`${ATT_BADGE[r.attendance_status] || ATT_BADGE.not_checked_in} border`}>
                      {r.attendance_status.replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <Badge className={`${STATUS_BADGE[r.status] || STATUS_BADGE.pending} border`}>
                      {r.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-xs text-neutral-600">
                    {r.opened_at ? new Date(r.opened_at).toLocaleString() : '-'}
                  </td>
                  <td className="p-3 text-xs text-neutral-600">
                    {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '-'}
                  </td>
                  <td className="p-3">
                    <Button size="sm" variant="outline"
                              onClick={(e) => { e.stopPropagation(); openDetail(r); }}
                              data-testid={`respondent-view-btn-${r.invitation_id}`}
                    >View response</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto"
                        data-testid="respondent-detail-dialog">
          <DialogHeader>
            <DialogTitle>
              {detail?.row?.full_name || 'Response detail'}
            </DialogTitle>
          </DialogHeader>
          {detail?.loading ? (
            <div className="p-4 text-center text-neutral-500">Loading...</div>
          ) : detail?.error ? (
            <div className="p-4 text-center text-neutral-500"
                  data-testid="respondent-detail-error">
              {detail.error}
            </div>
          ) : detail?.detail ? (
            <div className="space-y-4 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={`${STATUS_BADGE[detail.detail.status] || STATUS_BADGE.pending} border`}>
                  {detail.detail.status}
                </Badge>
                {detail.detail.is_test ? (
                  <Badge className="bg-yellow-50 text-yellow-800 border-yellow-200 border"
                          data-testid="respondent-detail-test-badge">test</Badge>
                ) : null}
                {detail.detail.is_migrated_from_pre_bundle89 ? (
                  <Badge className="bg-red-50 text-red-800 border-red-200 border"
                          data-testid="respondent-detail-migrated-badge">
                    pre-Bundle-89 migrated
                  </Badge>
                ) : null}
                {detail.detail.audit_only ? (
                  <Badge className="bg-neutral-100 text-neutral-700 border-neutral-200 border"
                          data-testid="respondent-detail-audit-only-badge">
                    audit only
                  </Badge>
                ) : null}
                {detail.detail.submitted_at ? (
                  <span className="text-xs text-neutral-500 ml-auto">
                    Submitted {new Date(detail.detail.submitted_at).toLocaleString()}
                  </span>
                ) : null}
              </div>
              {detail.detail.drift_reasons?.length ? (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 flex gap-2 items-start"
                      data-testid="respondent-detail-drift-banner">
                  <AlertTriangle className="h-4 w-4 text-red-700 mt-0.5" />
                  <div>
                    <div className="text-red-800 font-semibold text-xs uppercase tracking-widest">
                      Response carries pre-Bundle-89 values
                    </div>
                    <ul className="text-xs text-red-800 list-disc ml-4 mt-1">
                      {detail.detail.drift_reasons.map((r) => (
                        <li key={r}>{_driftLabel(r)}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : null}
              <div className="flex items-center justify-between">
                <div className="text-xs text-neutral-500">
                  {detail.unmasked
                    ? 'PII is currently VISIBLE. Refresh or reopen to remask.'
                    : 'Contact fields (email / phone / IG) are masked. Unmask requires re-confirm.'}
                </div>
                {!detail.unmasked ? (
                  <Button size="sm" variant="outline"
                            onClick={requestUnmask}
                            data-testid="respondent-detail-unmask-btn">
                    <Eye className="h-3 w-3 mr-1" /> Unmask PII
                  </Button>
                ) : (
                  <Button size="sm" variant="outline"
                            onClick={() => openDetail(detail.row)}
                            data-testid="respondent-detail-remask-btn">
                    <EyeOff className="h-3 w-3 mr-1" /> Re-mask
                  </Button>
                )}
              </div>
              <div className="border border-neutral-200 rounded-md divide-y"
                    data-testid="respondent-detail-answers">
                {(detail.detail.answers || []).length === 0 ? (
                  <div className="p-3 text-neutral-500 text-xs">
                    No answers on this response yet.
                  </div>
                ) : (detail.detail.answers || []).map((a) => (
                  <div key={a.key} className="p-3"
                        data-testid={`respondent-answer-${a.key}`}>
                    <div className="text-xs uppercase tracking-widest text-neutral-500">
                      {a.label}
                    </div>
                    <div className={`mt-1 text-sm break-words ${a.drift_reason ? 'text-red-800' : 'text-primary-900'}`}>
                      {a.is_pii && !detail.unmasked
                        ? String(a.masked_value ?? '')
                        : (typeof a.value === 'object'
                            ? JSON.stringify(a.value)
                            : String(a.value ?? ''))}
                      {a.is_pii ? (
                        <span className="ml-2 text-xs text-neutral-400">
                          [PII]
                        </span>
                      ) : null}
                      {a.drift_reason ? (
                        <span className="ml-2 text-xs text-red-700">
                          [drift]
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-4 text-center text-neutral-500">
              No detail available.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSurveyRespondentsPage;
